import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { ResumeDto } from "@reactive-resume/dto";
import { CustomSectionGroup, defaultSection, SectionKey } from "@reactive-resume/schema";
import { removeItemInLayout } from "@reactive-resume/utils";
import _set from "lodash.set";
import { temporal, TemporalState } from "zundo";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStoreWithEqualityFn } from "zustand/traditional";

import { debouncedUpdateResume } from "../services/resume";

type ResumeStore = {
  resume: ResumeDto;

  // Actions
  setValue: (path: string, value: unknown) => void;

  // Target job (top-level resume fields, saved by the Tailor tab via /resume/:id/match/job)
  setTargetJob: (job: {
    jobCompanyName?: string;
    jobTitle?: string;
    jobDescription?: string;
  }) => void;

  // Custom Section Actions
  addSection: () => void;
  removeSection: (sectionId: SectionKey) => void;

  // Preview Actions (for AI suggestions)
  previewOverlay: Record<string, any> | null;
  setPreviewOverlay: (path: string, value: unknown) => void;
  clearPreviewOverlay: () => void;
  applyPreviewOverlay: () => void;
};

export const useResumeStore = create<ResumeStore>()(
  temporal(
    immer((set) => ({
      resume: {} as ResumeDto,
      setValue: (path, value) => {
        set((state) => {
          if (path === "visibility") {
            state.resume.visibility = value as "public" | "private";
          } else {
            state.resume.data = _set(state.resume.data, path, value);
          }

          debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
        });
      },
      setTargetJob: (job) => {
        // Top-level fields are saved by the Tailor tab (/resume/:id/match/job);
        // here we only keep the in-memory store in sync so every AI feature reads
        // the same target job during the session (single source of truth).
        set((state) => {
          if (job.jobCompanyName !== undefined) state.resume.jobCompanyName = job.jobCompanyName;
          if (job.jobTitle !== undefined) state.resume.jobTitle = job.jobTitle;
          if (job.jobDescription !== undefined) state.resume.jobDescription = job.jobDescription;
        });
      },
      addSection: () => {
        const section: CustomSectionGroup = {
          ...defaultSection,
          id: createId(),
          name: t`Custom Section`,
          items: [],
        };

        set((state) => {
          const lastPageIndex = state.resume.data.metadata.layout.length - 1;
          state.resume.data.metadata.layout[lastPageIndex][0].push(`custom.${section.id}`);
          state.resume.data = _set(state.resume.data, `sections.custom.${section.id}`, section);

          debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
        });
      },
      removeSection: (sectionId: SectionKey) => {
        if (sectionId.startsWith("custom.")) {
          const id = sectionId.split("custom.")[1];

          set((state) => {
            removeItemInLayout(sectionId, state.resume.data.metadata.layout);
            delete state.resume.data.sections.custom[id];

            debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
          });
        }
      },

      // Preview overlay functionality
      previewOverlay: null,
      setPreviewOverlay: (path, value) => {
        set((state) => {
          // Store the preview as an overlay without modifying the actual resume
          state.previewOverlay = {
            path,
            value,
          };
        });
      },
      clearPreviewOverlay: () => {
        set((state) => {
          state.previewOverlay = null;
        });
      },
      applyPreviewOverlay: () => {
        set((state) => {
          if (state.previewOverlay) {
            const { path, value } = state.previewOverlay;
            // Apply the preview to actual resume state
            if (path === "visibility") {
              state.resume.visibility = value as "public" | "private";
            } else {
              state.resume.data = _set(state.resume.data, path, value);
            }
            // Clear the overlay
            state.previewOverlay = null;
            // Save the changes
            debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
          }
        });
      },
    })),
    {
      limit: 100,
      wrapTemporal: (fn) => devtools(fn),
      partialize: ({ resume }) => ({ resume }),
    },
  ),
);

export const useTemporalResumeStore = <T>(
  selector: (state: TemporalState<Pick<ResumeStore, "resume">>) => T,
  equality?: (a: T, b: T) => boolean,
) => useStoreWithEqualityFn(useResumeStore.temporal, selector, equality);

/**
 * Helper function to get a value from the resume with preview overlay support
 * If there's an active preview for the given path, returns the preview value
 * Otherwise returns the actual resume value
 */
export const getResumeValueWithPreview = (
  resume: ResumeDto,
  previewOverlay: Record<string, any> | null,
  path: string,
): any => {
  // If there's a preview overlay for this path, return the preview value
  if (previewOverlay && previewOverlay.path === path) {
    return previewOverlay.value;
  }

  // Otherwise return the actual value from the resume
  const pathParts = path.split(".");
  let value: any = resume.data;

  for (const part of pathParts) {
    if (value === undefined || value === null) return undefined;
    value = value[part];
  }

  return value;
};
